import { useEffect, useRef } from 'react'

import { getSession } from '@/shared/services/session/compat'

import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { authPaths, toRelativeCallbackPath } from '@/shared/lib/routing/auth'
import ErrorScreen from '@/shared/ui/ErrorScreen'

import {
  AuthBootstrapShell,
  completePostAuthNavigation
} from './shared'

export { AuthSignInPage } from './AuthSignInPage'
export {
  AuthSignUpPage,
  AuthForgotPasswordPage,
  AuthResetPasswordPage,
  AuthConfirmationPage,
  AuthResendConfirmationPage
} from './AuthSubRoutePage'

/** @deprecated Use AuthSignInPage */
export { AuthSignInPage as AuthSignInRedirect } from './AuthSignInPage'

const catalog = {
  Configuration: {
    statusCode: 500,
    title: 'Configuração de login indisponível',
    description:
      'Encontramos um problema com a configuração do provedor de autenticação. Nossa equipe já foi avisada.',
    suggestions: [
      'Volte para o início e tente novamente em alguns minutos',
      'Caso use login social, experimente outro provedor disponível'
    ]
  },
  AccessDenied: {
    statusCode: 403,
    title: 'Acesso negado',
    description:
      'Seu usuário não possui permissão para continuar com essa ação. Talvez você precise de outro perfil.',
    suggestions: [
      'Verifique com o administrador se seu e-mail tem o acesso necessário',
      'Experimente entrar com outra conta com permissões adequadas'
    ]
  },
  CredentialsSignin: {
    statusCode: 401,
    title: 'Não conseguimos confirmar seus dados',
    description:
      'Login ou senha estão incorretos ou não conseguimos validar a conta. Fique tranquilo, isso acontece!',
    suggestions: [
      'Verifique se as credenciais foram digitadas corretamente',
      'Se esqueceu a senha, faça a redefinição antes de tentar novamente'
    ]
  },
  OAuthAccountNotLinked: {
    statusCode: 409,
    title: 'Conta já vinculada a outro login',
    description:
      'Você tentou entrar com um provedor diferente daquele usado originalmente. Por segurança, não podemos fazer a troca automática.',
    suggestions: [
      'Entre com o provedor utilizado no primeiro acesso',
      'Se preferir unificar, faça login com o método antigo e vincule a nova conta nas configurações'
    ]
  },
  SessionRequired: {
    statusCode: 401,
    title: 'Sessão expirada',
    description:
      'Sua sessão perdeu a validade. Basta entrar novamente para continuar de onde parou.',
    suggestions: [
      'Clique no botão abaixo para iniciar uma nova sessão',
      'Ative a opção “manter conectado” para evitar expirar rapidamente'
    ]
  },
  NoTicket: {
    statusCode: 403,
    title: 'Você ainda não tem acesso a este evento',
    description:
      'Sua conta foi autenticada, mas não está liberada para a gamificação deste evento. Para participar, você precisa de um convite ou de um ingresso vinculado ao evento.',
    suggestions: [
      'Peça ao organizador o link de convite ou confirme se o ingresso foi emitido para o mesmo e-mail usado no login',
      'Se você comprou o ingresso agora, aguarde alguns minutos e tente novamente',
      'Use outra conta que já tenha acesso liberado ao evento'
    ]
  },
  default: {
    statusCode: 500,
    title: 'Tivemos um contratempo ao autenticar você',
    description:
      'Ocorreu uma falha inesperada durante o login. Nossa equipe já recebeu o alerta.',
    suggestions: [
      'Espere alguns instantes e tente novamente',
      'Caso o problema continue, entre em contato com o suporte informando o código do erro'
    ]
  }
} as const

type AuthErrorViewProps = {
  errorCode?: string
  callbackUrl?: string
  referer?: string
  message?: string
  digitalProductId?: string
}

export const AuthErrorView = ({
  errorCode = 'default',
  callbackUrl = '',
  referer = '',
  message = ''
}: AuthErrorViewProps) => {
  const lookup =
    catalog[errorCode as keyof typeof catalog] ?? catalog.default
  const detailItems = []

  if (callbackUrl) {
    detailItems.push({
      label: 'Destino original',
      value: callbackUrl
    })
  }

  if (referer) {
    detailItems.push({
      label: 'Página anterior',
      value: referer
    })
  }

  if (message) {
    detailItems.push({
      label: 'Mensagem do provedor',
      value: message
    })
  }

  return (
    <ErrorScreen
      statusCode={lookup.statusCode}
      title={lookup.title}
      description={lookup.description}
      details={detailItems}
      suggestions={[...lookup.suggestions]}
      actionHref="/"
      actionLabel="Voltar para o início"
      errorCode={errorCode}
      illustration="/misc/wave-bg-purple.svg"
    />
  )
}

type TenantAuthPageProps = {
  digitalProductId?: string
}

export const AuthRefreshSession = ({
  digitalProductId: digitalProductIdProp
}: TenantAuthPageProps = {}) => {
  const router = useAppRouter()
  const refreshStartedRef = useRef(false)

  const redirectToSignIn = (normalizedCallback: string) => {
    router.replace(authPaths.signIn({ callbackUrl: normalizedCallback }))
  }

  const refreshSession = async () => {
    if (!router.isReady) return

    const queryDigitalProductId = router.query?.digitalProductId
    const eventSlug =
      digitalProductIdProp ||
      (Array.isArray(queryDigitalProductId)
        ? queryDigitalProductId[0]
        : queryDigitalProductId)
    const resolvedEventSlug =
      typeof eventSlug === 'string' ? eventSlug : undefined

    const queryToken = router.query?.token
    const queryCallbackUrl = router.query?.callbackUrl
    const token =
      Array.isArray(queryToken) && queryToken.length ? queryToken[0] : queryToken
    const normalizedCallback = toRelativeCallbackPath(
      queryCallbackUrl != null
        ? String(
            Array.isArray(queryCallbackUrl)
              ? queryCallbackUrl[0]
              : queryCallbackUrl
          )
        : undefined
    )

    if (!token) {
      redirectToSignIn(normalizedCallback)
      return
    }

    try {
      const { signIn, signOut } = await import('@/shared/services/session/compat')
      const { getAccessToken } = await import('@/shared/services/session/instance')

      await signOut({ redirect: false })

      // O app nativo entrega um access token que ele já possui. Era o provider
      // `refresh-session` do next-auth; no broker é `POST /auth/login/token`,
      // que reaproveita o token e só acrescenta o cookie de refresh que o fluxo
      // de browser precisa — em vez de emitir um par novo à toa.
      const result = await signIn('refresh-session', {
        token,
        callbackUrl: normalizedCallback,
        redirect: false
      })

      if (result?.ok) {
        await completePostAuthNavigation({
          router,
          returnTo: toRelativeCallbackPath(normalizedCallback),
          // O token não vem mais do objeto de sessão: ele vive em memória e não
          // chega à UI. Aqui a leitura é legítima — a ponte com o app nativo
          // não passa por uma requisição HTTP que o cliente autenticado envolva.
          accessToken: getAccessToken() ?? undefined,
          eventSlug: resolvedEventSlug
        })
        return
      }

      redirectToSignIn(normalizedCallback)
    } catch {
      redirectToSignIn(normalizedCallback)
    }
  }

  useEffect(() => {
    if (refreshStartedRef.current) return
    if (!router.isReady) return

    refreshStartedRef.current = true
    void refreshSession()
  }, [digitalProductIdProp, router.isReady, router.asPath])

  return <AuthBootstrapShell />
}
